import { useEffect } from "react";

/**
 * The class that lets a route scroll the DOCUMENT instead of an inner pane.
 *
 * Paired with a rule in index.css. The two halves are a contract: the hook
 * writes this class, the stylesheet relaxes `html`, `body` and `#root` for it.
 * Rename one and the page silently stops scrolling again, which is why
 * usePageScroll.test.ts asserts that the stylesheet still carries a rule for
 * this exact name.
 */
export const PAGE_SCROLL_CLASS = "page-scroll";

/** Just the slice of the element we touch, so a test can pass a stub. */
type ClassListHolder = {
  classList: { add(token: string): void; remove(token: string): void };
};

/**
 * Turns document scrolling on, and returns the undo.
 *
 * Exported for the test; components use the hook below.
 */
export function enablePageScroll(root: ClassListHolder): () => void {
  root.classList.add(PAGE_SCROLL_CLASS);
  return () => root.classList.remove(PAGE_SCROLL_CLASS);
}

/**
 * Let THIS route scroll the document.
 *
 * WHY ANY ROUTE NEEDS THIS. index.css locks `html`, `body` and `#root` to the
 * viewport with `overflow: hidden`, because the authenticated app shell
 * scrolls its own content pane. A route rendered OUTSIDE that shell — the
 * standalone checkout at /pay/mercadopago, for one — has no such pane, so on
 * any viewport shorter than its content there is nothing to scroll and the
 * page simply ends. A paying customer on a phone could not reach the Mercado
 * Pago method rows to choose a card, which is the bug this exists to fix.
 *
 * Opt-in, and undone on unmount, so the shell's own locking is untouched
 * everywhere else.
 */
export function usePageScroll(): void {
  useEffect(() => enablePageScroll(document.documentElement), []);
}
